/**
 * The document the customer actually receives.
 *
 * Pure: job data in, a finished document model out. No React, no formatting, no
 * currency symbols. The renderer decides how it looks; this decides what is on
 * it — which is the part that must not vary between the screen, the print, the
 * PDF and the email.
 *
 * Three product rules are enforced here rather than in a template:
 *
 * 1. **The document's language is the CUSTOMER's, not the estimator's** (FR54).
 *    A Riyadh shop with an English-speaking estimator still sends Arabic to an
 *    Arabic customer. Nobody should have to remember to switch.
 * 2. **A suppressed line still appears, marked, at zero.** Deleting it would
 *    hide that the shop considered the operation and chose not to charge — which
 *    is exactly the question that comes up in a dispute.
 * 3. **An issued document carries its version and hash.** An approval against a
 *    document that can still change proves nothing.
 */

import type { CalcResult, EstimateLine } from '../calc/types.js';

export type Ending = 'invoice' | 'printout';

export interface DocumentInput {
  reference: string;
  shopName: string;
  customer: { name: string; needsApproval: boolean; terms: string; language?: string };
  vehicle: string;
  impact?: string;
  lines: EstimateLine[];
  totals: CalcResult;
  currencyCode: string;
  /** The estimator's interface language — the fallback when the customer has none. */
  estimatorLanguage: string;
  issued: { at: string; version: number; hash: string } | null;
}

export interface DocumentRow {
  no: number;
  operation: string;
  description: string;
  partNumber: string | null;
  quantity: number;
  /** Unit price. Null when this line carries no part at all. */
  unitPrice: number | null;
  /** True when the line consumes no part — a repair, an R&I, an inspection. */
  partless: boolean;
  hours: number;
  /** What the parts on this row contribute. Zero for a suppressed row. */
  amount: number;
  suppressed: boolean;
  /**
   * The evidence behind this line, when it came from an AI proposal.
   * Undefined for a hand-added line, a refinish line, or one issued before
   * this existed — never fabricated to fill the gap. Deliberately just the
   * photograph and the plain observation text: no confidence, no model name,
   * no rationale. A customer is not the audience for the model's own words
   * about itself, and a percentage on this screen invites an argument with
   * a number rather than with what the photograph actually shows.
   */
  whyThis?: { observations: string[]; photos: string[] };
}

export interface DocumentTotalRow {
  label: string;
  amount: number;
  /** Rendered heavier, and last. */
  emphasis?: boolean;
}

export interface EstimateDocument {
  kind: Ending;
  title: string;
  reference: string;
  shopName: string;
  customerName: string;
  terms: string;
  vehicle: string;
  impact: string | null;
  /** The language this document must be written in. */
  language: string;
  currencyCode: string;
  rows: DocumentRow[];
  totals: DocumentTotalRow[];
  grandTotal: number;
  /** Null while the estimate is still a draft. */
  issued: { at: string; version: number; hash: string } | null;
  /** What the recipient is being asked to do. Null for a printout. */
  callToAction: string | null;
  /** Lines shown but charged at nothing, so the reader is told why. */
  suppressedNote: string | null;
}

export function buildDocument(input: DocumentInput): EstimateDocument {
  const ending: Ending = input.customer.needsApproval ? 'invoice' : 'printout';
  const issued = input.issued;

  const rows: DocumentRow[] = input.lines.map((line, i) => {
    // A repair or an R&I consumes no part. Printing "$0.00" against it reads as
    // "we are doing this for free", which is the opposite of what it means —
    // the charge is in the hours. A dash says "not applicable" and is honest.
    const partless = line.partType === 'none'
      || (line.partPrice === 0 && !line.partNumber);
    return {
    no: i + 1,
    operation: line.operation,
    description: line.description,
    partNumber: line.partNumber ?? null,
    quantity: line.quantity,
    unitPrice: partless ? null : line.partPrice,
    partless,
    hours: line.hours,
    // Parts only. Labour is priced by rate and reported in the totals block —
    // recomputing it here would be a second calculation engine, and the two
    // would eventually disagree in front of a customer.
    amount: line.suppressed ? 0 : line.partPrice * line.quantity,
    suppressed: Boolean(line.suppressed),
  };
  });

  const t = input.totals;
  const totals: DocumentTotalRow[] = [{ label: 'Parts', amount: t.partsTotal }];
  for (const [kind, entry] of Object.entries(t.labour)) {
    // A labour category nobody worked is noise on a customer's document.
    if (entry.amount !== 0 || entry.hours !== 0) {
      totals.push({ label: `${kind} labour`, amount: entry.amount });
    }
  }
  if (t.paintMaterials !== 0) totals.push({ label: 'Paint materials', amount: t.paintMaterials });
  if (t.shopMaterials !== 0) totals.push({ label: 'Shop materials', amount: t.shopMaterials });
  for (const tax of t.taxes) {
    totals.push({ label: `${tax.name} ${tax.rate}%`, amount: tax.amount });
  }
  totals.push({ label: 'Total', amount: t.grandTotal, emphasis: true });

  const suppressed = rows.filter((r) => r.suppressed).length;

  return {
    kind: ending,
    title: !issued ? 'Repair estimate — draft'
      : ending === 'invoice' ? 'Repair estimate — for approval'
      : 'Repair estimate',
    reference: input.reference,
    shopName: input.shopName,
    customerName: input.customer.name,
    terms: input.customer.terms,
    vehicle: input.vehicle,
    impact: input.impact ?? null,
    language: input.customer.language ?? input.estimatorLanguage,
    currencyCode: input.currencyCode,
    rows,
    totals,
    grandTotal: t.grandTotal,
    issued,
    callToAction: !issued ? null
      : ending === 'invoice'
        ? 'Reply approved, not approved, or partly approved with what to leave out.'
        : null,
    suppressedNote: suppressed === 0 ? null
      : `${suppressed} line${suppressed === 1 ? '' : 's'} shown but not charged.`,
  };
}
